import axios from 'axios';
import { API_URL } from '@/common/config';
import reportesCache from './reportesCache';

class ReportesApiService {
  constructor() {
    this.header = {
      headers: {
        'Content-Type': 'application/json',
        token: localStorage.getItem('token'),
      },
    };
    // Para gestionar la cancelación del job actual
    this.currentEventSource = null;
    this.currentJobId = null;
    this.currentProgressTimer = null;
    this.currentStopEstimatedProgress = null;
    this.currentTimeouts = []; // Array para guardar todos los timeouts activos
  }

  async obtenerTiposDeServicio(tiposOperatorias) {
    try {
      const response = await axios.get(
        `${API_URL}api/tipoServicios`,
        this.header
      );
      return response.data.tipoServicios.filter((e) =>
        tiposOperatorias.includes(e.nombre)
      );
    } catch (error) {
      console.error('Error al obtener tipos de servicio', error);
      throw error;
    }
  }

  async obtenerEstadosSolicitud(estadosPermitidos) {
    try {
      const url = `${API_URL}api/solicitudEstado`;

      const response = await axios.get(url, this.header);
      return [
        { id: 'todas', nombre: 'Todas' },
        ...response.data.solicitudEstado.filter((e) =>
          estadosPermitidos.includes(e.nombre)
        ),
      ];
    } catch (error) {
      console.error('Error al obtener los estados de solicitud:', error);
      throw error;
    }
  }

  async obtenerFideicomisos() {
    try {
      const response = await axios.get(`${API_URL}api/admin/unidadGestion`, {
        headers: {
          token: localStorage.getItem('token'),
        },
      });
      return response.data.fideicomisos;
    } catch (error) {
      console.error('Error al obtener fideicomisos:', error);
      throw error;
    }
  }

  async obtenerOperatorias(
    fideicomisoFilt,
    tiposServiciosFilt,
    page = 1,
    limit = 10,
    fideicomisos = []
  ) {
    if (!tiposServiciosFilt) {
      return [];
    }

    try {
      // Obtener IDs de los fideicomisos
      let fideicomisosIds = [];

      // Si no hay filtro de fideicomiso o está vacío, usar todos por defecto
      if (!fideicomisoFilt || (Array.isArray(fideicomisoFilt) && fideicomisoFilt.length === 0)) {
        // No hay selección - usar todos los fideicomisos por defecto
        fideicomisosIds = fideicomisos.map((f) => f.id);
      } else {
        // Convertir a array si es string
        const fideicomisoArray = Array.isArray(fideicomisoFilt)
          ? fideicomisoFilt
          : [fideicomisoFilt];

        if (fideicomisoArray.length > 0) {
          // Obtener IDs específicos
          fideicomisosIds = fideicomisoArray
            .map((nombre) => {
              const fideicomiso = fideicomisos.find(
                (el) => el.nombre === nombre
              );
              return fideicomiso ? fideicomiso.id : null;
            })
            .filter((id) => id !== null);
        }
      }

      // Obtener ID del tipo de servicio
      let tiposServiciosIds = [];
      if (tiposServiciosFilt) {
        tiposServiciosIds = [tiposServiciosFilt];
      }

      // Construir URL con parámetros
      let url = `${API_URL}api/v1/lineas-creditos/filter?page=${page}&limit=${limit}`;

      if (tiposServiciosIds.length > 0) {
        url += `&tipo_servicio_id=${tiposServiciosIds
          .map((id) => String(id))
          .join(',')}`;
      }

      if (fideicomisosIds.length > 0) {
        url += `&fideicomiso_id=${fideicomisosIds
          .map((id) => String(id))
          .join(',')}`;
      }

      const response = await axios.get(url, {
        headers: {
          token: localStorage.getItem('token'),
        },
      });

      const listado =
        response.data.response && response.data.response.data
          ? response.data.response.data
          : [];
      return [{ id: 'todas', nombre: 'Todas' }, ...listado];
    } catch (error) {
      console.error('Error al obtener operatorias:', error);
      return [{ id: 'todas', nombre: 'Todas' }];
    }
  }

  async obtenerParametros(tipoServicioId) {
    try {
      const response = await axios.get(
        `${API_URL}api/tipoParametrosOpreatoria/${tipoServicioId}/cliente`,
        {
          headers: {
            token: localStorage.getItem('token'),
          },
        }
      );
      return response.data.listaParametros.filter((item) =>
        item.clave.toLowerCase().includes('monto')
      );
    } catch (error) {
      console.error('Error al obtener parámetros:', error);
      throw error;
    }
  }

  async obtenerDatosReportes(parametros) {
    const url = `${API_URL}api/v1/reportes`;

    try {
      const response = await axios.get(url, {
        params: parametros,
        headers: {
          'Content-Type': 'application/json',
          token: localStorage.getItem('token'),
        },
      });

      if (response.data.status === 'success') {
        return {
          datos: response.data.response,
          totalItems:
            (response.data.response.pagination &&
              response.data.response.pagination.totalRecords) ||
            response.data.response.total ||
            0,
        };
      } else {
        throw new Error('Error en la respuesta del servidor');
      }
    } catch (error) {
      console.error('Error al obtener datos de reportes:', error);
      throw error;
    }
  }

  async obtenerListaOperatorias() {
    try {
      const url = `${API_URL}api/servicio`;
      const { data } = await axios.get(url, this.header);

      const listaOperatorias = data.lineaCreditos.reduce((acc, el) => {
        if (el.tipo_servicio_id.id === 20) {
          return acc.concat({ text: el.nombre, value: el.id });
        }
        return acc;
      }, []);
      listaOperatorias.unshift({ text: 'General', value: '' });
      return listaOperatorias;
    } catch (error) {
      console.error('Error al obtener lista de operatorias:', error);
      throw error;
    }
  }

  async obtenerInformeOperatoria(parametrosInforme) {
    try {
      const query = {
        method: 'POST',
        headers: {
          token: localStorage.getItem('token'),
        },
        url: `${API_URL}api/reportes/operatoria`,
        data: parametrosInforme,
      };

      const { data } = await axios(query);
      return data;
    } catch (error) {
      console.error('Error al obtener informe de operatoria:', error);
      throw error;
    }
  }

  async obtenerOperatoriasPorFideicomiso(fideicomisoId) {
    try {
      const url = `${API_URL}api/servicio/tipoServicioXfideicomiso/list/${fideicomisoId}`;

      const response = await axios.get(url, {
        headers: {
          'Content-Type': 'application/json',
          token: localStorage.getItem('token'),
        },
      });

      return response.data;
    } catch (error) {
      console.error('Error al obtener operatorias por fideicomiso:', error);
      throw error;
    }
  }

  async obtenerInfoMoraFinanciamiento(parametros) {
    try {
      const url = `${API_URL}api/v1/reportes-especiales/financiamiento`;

      const response = await axios.get(url, {
        params: parametros,
        headers: {
          'Content-Type': 'application/json',
          token: localStorage.getItem('token'),
        },
      });

      return {
        datos: {
          resultados: response.data.response.data,
          totales: response.data.response.totales,
        },
        totalItems: response.data.response.total,
        page: response.data.response.page,
        limit: response.data.response.limit,
      };
    } catch (error) {
      console.error('Error al obtener info mora financiamiento:', error);
      throw error;
    }
  }

  async obtenerInfoCobranzasProyectadas(parametros) {
    try {
      const url = `${API_URL}api/v1/reportes-especiales/cobranzas-proyectadas`;

      const response = await axios.get(url, {
        params: parametros,
        headers: {
          'Content-Type': 'application/json',
          token: localStorage.getItem('token'),
        },
      });

      return {
        datos: {
          resultados: response.data.response.data,
          totales: response.data.response.totales,
        },
        totalItems: response.data.response.total,
        page: response.data.response.page,
        limit: response.data.response.limit,
      };
    } catch (error) {
      console.error('Error al obtener info cobranzas proyectadas:', error);
      throw error;
    }
  }

  async obtenerInfoCobranzas(parametros, onProgress = null) {
    try {
      const url = `${API_URL}api/v1/reportes-especiales/info-cobranzas`;

      // PRIMERO: Verificar si hay datos en cache del navegador
      const cachedData = reportesCache.get(parametros);
      if (cachedData) {
        console.log('🚀 Usando datos del cache del navegador');
        if (onProgress) {
          onProgress({
            progress: 100,
            processedRecords: cachedData.totalItems || 0,
            totalRecords: cachedData.totalItems || 0,
            estimatedTimeLeft: 0,
            statusMessage: 'Datos cargados desde cache del navegador'
          });
        }
        return cachedData;
      }

      console.log('ℹ️ No hay cache disponible, consultando al servidor...');

      // Ya no se usa modo asíncrono con SSE - el backend procesa todo síncronamente con chunking
      const params = parametros;

      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('No se encontró token de autenticación');
      }

      // Headers con token para autenticación
      const headers = {
        'Authorization': `Bearer ${token}`,
        'token': token, // Mantener token header por compatibilidad
        'Accept': 'application/json, text/plain, */*',
        'Content-Type': 'application/json'
      };

      console.log('🔍 obtenerInfoCobranzas - Headers:', headers, 'parámetros:', params);
      console.log('🔍 API_URL configurada:', API_URL);

      const response = await axios.get(url, {
        params: params,
        headers: headers,
        timeout: 300000, // 5 minutos timeout
      });

      console.log('🔍 Respuesta completa del backend:', {
        hasData: !!response.data.data,
        hasResponseData: !!(response.data.response && response.data.response.data),
        totalItems: response.data.total || response.data.totalItems,
        statusCode: response.status,
        'response.data.response.pagination': response.data.response && response.data.response.pagination,
        'response.data.total': response.data.total,
        'response.data.totalItems': response.data.totalItems,
        fullResponse: response.data
      });

      // El backend puede devolver en response.response.data o response.data.data
      const datosDirectos = (response.data.response && response.data.response.data) || response.data.data || [];

      // Extraer totalItems - IMPORTANTE: response.data.response.total es el campo correcto
      const totalItems = (response.data.response && response.data.response.total) ||
                         (response.data.response && response.data.response.pagination && response.data.response.pagination.totalRecords) ||
                         response.data.total ||
                         response.data.totalItems ||
                         datosDirectos.length;

      console.log('📋 Procesamiento con chunking del backend - datos recibidos:', datosDirectos.length, 'registros');

      const resultado = {
        datos: {
          resultados: datosDirectos,
          totales: (response.data.response && response.data.response.totales) || response.data.totales || {},
        },
        totalItems: totalItems,
        page: (response.data.response && response.data.response.pagination && response.data.response.pagination.currentPage) || response.data.page || 1,
        limit: (response.data.response && response.data.response.pagination && response.data.response.pagination.limit) || response.data.limit || parametros.limit || 50,
        response: {
          data: datosDirectos,
        }
      };

      // Guardar en cache del navegador
      reportesCache.set(parametros, resultado);

      console.log('✅ Resultado procesado:', {
        cantidadDatos: resultado.datos.resultados.length,
        totalItems: resultado.totalItems,
        page: resultado.page,
        limit: resultado.limit
      });

      return resultado;

    } catch (error) {
      console.error('Error al obtener info cobranzas:', error);
      throw error;
    }
  }

  async obtenerInfoCobranzasConSSE(jobInfo, onProgress, parametros) {
    return new Promise((resolve, reject) => {
      try {
        console.log('📡 Conectando a SSE para job:', jobInfo.jobId);

        // Obtener estimación de registros total (se puede pasar como parámetro del jobInfo)
        const totalEstimado = jobInfo.estimatedRecords || 957;

        // Construir URL del SSE endpoint
        const sseUrl = `${API_URL}api/v1/reportes-especiales/info-cobranzas/jobs/${jobInfo.jobId}/stream`;
        const token = localStorage.getItem('token');

        // SSE usa query parameter para autenticación (EventSource no permite headers custom)
        const fullSseUrl = sseUrl + `?token=${encodeURIComponent(token)}`;

        console.log('📡 URL completa SSE:', fullSseUrl);
        console.log('📡 JobId:', jobInfo.jobId);

        // Crear EventSource (no se pueden agregar headers custom en EventSource)
        const eventSource = new EventSource(fullSseUrl);
        console.log('✅ EventSource creado, readyState:', eventSource.readyState);

        // Guardar referencias para poder cancelar
        this.currentEventSource = eventSource;
        this.currentJobId = jobInfo.jobId;
        this.currentTimeouts = []; // Limpiar timeouts anteriores

        // Timeout de seguridad de 5 minutos
        const timeoutId = setTimeout(() => {
          stopEstimatedProgress();
          clearTimeout(cacheCheckTimeout);
          eventSource.close();
          reject(new Error('Timeout: El proceso tardó más de 5 minutos'));
        }, 5 * 60 * 1000);
        this.currentTimeouts.push(timeoutId);

        // Progreso estimado basado en tiempo
        let startTime = Date.now();
        // Para limit=-1, usar una duración estimada más larga
        let estimatedDuration = jobInfo.estimatedTimeMs || (parametros.limit === -1 ? 30000 : 10000); // 30 segundos para "Todos", 10 para otros
        let progressTimer = null;
        let completedReceived = false;

        // Timeout para intentar obtener datos del cache si no llega completed
        // Para limit=-1, usar un timeout más largo
        const baseTimeout = parametros.limit === -1 ? 60000 : 15000; // 60 segundos para "Todos", 15 para otros
        const timeoutDuration = Math.max(baseTimeout, estimatedDuration + 10000);
        const cacheCheckTimeout = setTimeout(async () => {
          if (!completedReceived) {
            console.log('⏰ Timeout alcanzado, intentando obtener datos del cache...');
            stopEstimatedProgress();
            eventSource.close();
            clearTimeout(timeoutId);

            try {
              // Intentar obtener datos del endpoint de cache
              const cacheUrl = `${API_URL}api/v1/reportes-especiales/info-cobranzas/cached/${jobInfo.jobId}`;
              console.log('🗄️ Intentando obtener datos del cache:', cacheUrl);

              const cacheResponse = await fetch(cacheUrl, {
                headers: {
                  'Authorization': `Bearer ${localStorage.getItem('token')}`,
                  'token': localStorage.getItem('token'),
                  'Accept': 'application/json'
                }
              });

              if (cacheResponse.ok) {
                const cacheData = await cacheResponse.json();
                console.log('✅ Datos obtenidos del cache:', cacheData);

                // Debug detallado de la estructura
                console.log('🔍 TIMEOUT HANDLER - Debug detallado:', {
                  'cacheData.response': !!cacheData.response,
                  'cacheData.response.data': !!(cacheData.response && cacheData.response.data),
                  'cacheData.response.data.length': cacheData.response && cacheData.response.data ? cacheData.response.data.length : 'N/A',
                  'cacheData.response.total': cacheData.response && cacheData.response.total,
                  'cacheData.data': !!cacheData.data,
                  'cacheData.total': cacheData.total,
                  'DATOS_DETECTADOS': !!(cacheData.data || (cacheData.response && cacheData.response.data)),
                  'TOTAL_DETECTADO': cacheData.total || (cacheData.response && cacheData.response.total) || 'No encontrado'
                });

                const resultado = {
                  datos: {
                    resultados: cacheData.data || (cacheData.response && cacheData.response.data) || [],
                    totales: cacheData.totales || (cacheData.response && cacheData.response.totales) || {},
                  },
                  totalItems: cacheData.total || (cacheData.response && cacheData.response.total) || 0,
                  page: cacheData.page || (cacheData.response && cacheData.response.page) || 1,
                  limit: cacheData.limit || (cacheData.response && cacheData.response.limit) || 50,
                  totalTime: estimatedDuration,
                  processingStats: {
                    jobId: jobInfo.jobId,
                    duration: estimatedDuration,
                    recordsProcessed: cacheData.total || (cacheData.response && cacheData.response.total) || 0,
                    fromCache: true
                  }
                };

                // Guardar en cache del navegador
                reportesCache.set(parametros, resultado);

                resolve(resultado);
              } else {
                console.warn('⚠️ No se pudieron obtener datos del cache, intentando llamada síncrona...');

                // Intentar obtener datos con llamada síncrona como fallback
                try {
                  const syncParams = { ...parametros };
                  delete syncParams.subscribe; // Remover subscribe para llamada síncrona

                  console.log('🔄 Intentando llamada síncrona fallback con params:', syncParams);
                  console.log('🔄 URL:', url);

                  const syncResponse = await axios.get(url, {
                    params: syncParams,
                    headers: headers,
                  });

                  console.log('🔄 Respuesta síncrona completa:', syncResponse.data);

                  const syncDatosDirectos = syncResponse.data.data || (syncResponse.data.response && syncResponse.data.response.data);
                  console.log('🔄 syncDatosDirectos:', syncDatosDirectos);
                  console.log('🔄 syncDatosDirectos length:', syncDatosDirectos ? syncDatosDirectos.length : 'N/A');

                  if (syncDatosDirectos && Array.isArray(syncDatosDirectos) && syncDatosDirectos.length > 0) {
                    console.log('✅ Resolviendo con datos del fallback síncrono');
                    resolve({
                      datos: {
                        resultados: syncDatosDirectos,
                        totales: syncResponse.data.totales || (syncResponse.data.response && syncResponse.data.response.totales) || {},
                      },
                      totalItems: syncResponse.data.total || (syncResponse.data.response && syncResponse.data.response.total) || syncDatosDirectos.length,
                      page: syncResponse.data.page || (syncResponse.data.response && syncResponse.data.response.page) || 1,
                      limit: syncResponse.data.limit || (syncResponse.data.response && syncResponse.data.response.limit) || 50,
                      fromCache: false,
                      fromSyncFallback: true
                    });
                    return;
                  } else {
                    console.log('⚠️ No se encontraron datos en respuesta síncrona');
                  }
                } catch (syncError) {
                  console.error('❌ Error en llamada síncrona fallback:', syncError);
                  console.error('❌ Detalles del error:', syncError.response ? syncError.response.data : syncError.message);
                }

                // Si tampoco funciona la llamada síncrona, devolver datos vacíos
                resolve({
                  datos: { resultados: [], totales: {} },
                  totalItems: 0,
                  page: 1,
                  limit: 50,
                  fromCache: false
                });
              }
            } catch (cacheError) {
              console.error('❌ Error al obtener datos del cache:', cacheError);
              resolve({
                datos: { resultados: [], totales: {} },
                totalItems: 0,
                page: 1,
                limit: 50,
                fromCache: false
              });
            }
          }
        }, timeoutDuration); // Usar la duración calculada
        this.currentTimeouts.push(cacheCheckTimeout);

        // Función para iniciar progreso estimado
        const startEstimatedProgress = () => {
          if (progressTimer) clearInterval(progressTimer);

          progressTimer = setInterval(() => {
            const elapsed = Date.now() - startTime;
            const estimatedProgress = Math.min(95, (elapsed / estimatedDuration) * 100);
            const remainingTime = Math.max(0, estimatedDuration - elapsed);

            // Simular registros procesados basado en el progreso
            const processedEstimado = Math.floor((estimatedProgress / 100) * totalEstimado);

            if (onProgress) {
              onProgress({
                progress: estimatedProgress,
                processedRecords: processedEstimado,
                totalRecords: totalEstimado,
                estimatedTimeLeft: remainingTime / 1000,
                statusMessage: `Procesando consulta... ${processedEstimado}/${totalEstimado} registros`
              });
            }
          }, 1000); // Actualizar cada segundo
        };

        // Función para detener progreso estimado
        const stopEstimatedProgress = () => {
          if (progressTimer) {
            clearInterval(progressTimer);
            progressTimer = null;
          }
        };

        // Guardar referencia para poder detener desde cancelJob
        this.currentStopEstimatedProgress = stopEstimatedProgress;

        // Manejar conexión abierta
        eventSource.onopen = (event) => {
          console.log('✅ SSE conectado para job:', jobInfo.jobId);
          console.log('📡 EventSource readyState:', eventSource.readyState);
          if (onProgress) {
            onProgress({
              progress: 0,
              processedRecords: 0,
              totalRecords: totalEstimado,
              estimatedTimeLeft: estimatedDuration / 1000,
              statusMessage: 'Conectado al servidor, iniciando procesamiento...'
            });
          }
        };

        // Manejar mensajes de progreso
        eventSource.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data);
            console.log('📊 Progreso SSE recibido:', data);

            if (data.type === 'progress') {
              if (onProgress) {
                // Normalizar los nombres de campos que pueden venir del backend
                const processedRecords = data.processedRecords || data.processed || 0;
                const totalRecords = data.totalRecords || data.total || 0;
                const progress = data.progress || (totalRecords > 0 ? Math.round((processedRecords / totalRecords) * 100) : 0);
                const estimatedTimeLeft = data.estimatedTimeLeftMs ? data.estimatedTimeLeftMs / 1000 :
                                        (data.estimatedTimeRemaining ? data.estimatedTimeRemaining / 1000 : 0);

                onProgress({
                  progress: progress,
                  processedRecords: processedRecords,
                  totalRecords: totalRecords,
                  estimatedTimeLeft: estimatedTimeLeft,
                  statusMessage: data.message || data.statusMessage || `Procesando ${processedRecords}/${totalRecords}...`
                });
              }
            } else if (data.type === 'connected') {
              // Tratar evento 'connected' como inicio de progreso
              console.log('📡 SSE conectado - iniciando progreso estimado');
              if (onProgress) {
                onProgress({
                  progress: 0,
                  processedRecords: 0,
                  totalRecords: totalEstimado,
                  estimatedTimeLeft: estimatedDuration / 1000,
                  statusMessage: data.message || 'Conectado al servidor, iniciando procesamiento...'
                });
              }
              // Iniciar progreso estimado después de 2 segundos
              const startProgressTimeout = setTimeout(() => {
                console.log('⏱️ Iniciando progreso estimado basado en tiempo');
                startEstimatedProgress();
              }, 2000);
              this.currentTimeouts.push(startProgressTimeout);
            } else if (data.type === 'ping') {
              // Los pings mantienen la conexión activa, no necesitan acción especial
              console.log('💓 Ping recibido - conexión activa');
            } else if (data.type === 'completed') {
              console.log('✅ Procesamiento completado vía SSE:', data);
              completedReceived = true;
              stopEstimatedProgress();
              clearTimeout(timeoutId);
              clearTimeout(cacheCheckTimeout);
              eventSource.close();

              // Limpiar referencias
              this.currentEventSource = null;
              this.currentJobId = null;
              this.currentStopEstimatedProgress = null;

              // Verificar si los datos vienen directamente en el evento completed
              if (data.result && data.result.data && Array.isArray(data.result.data) && data.result.data.length > 0) {
                console.log('📦 Datos recibidos directamente en evento completed desde backend mejorado');
                const resultado = {
                  datos: {
                    resultados: data.result.data,
                    totales: data.result.totales || {},
                  },
                  totalItems: data.result.total || data.result.data.length,
                  page: data.result.page || 1,
                  limit: data.result.limit || 50,
                  totalTime: data.processingTimeMs || 0,
                  processingStats: {
                    jobId: jobInfo.jobId,
                    duration: data.processingTimeMs || 0,
                    recordsProcessed: data.totalRecords || data.result.data.length,
                    fromSSE: true
                  }
                };

                // Guardar en cache del navegador
                reportesCache.set(parametros, resultado);

                resolve(resultado);
                return;
              }

              // Fallback: usar cacheKey del evento completed para obtener los datos del caché
              const cacheKey = data.cacheKey || jobInfo.jobId;
              console.log('🗄️ No hay datos en evento completed, obteniendo del caché usando cacheKey:', cacheKey);

              const cacheUrl = `${API_URL}api/v1/reportes-especiales/info-cobranzas/cached/${cacheKey}`;
              console.log('🗄️ URL del caché:', cacheUrl);

              fetch(cacheUrl, {
                headers: {
                  'Authorization': `Bearer ${localStorage.getItem('token')}`,
                  'token': localStorage.getItem('token'),
                  'Accept': 'application/json'
                }
              }).then(async cacheResponse => {
                if (cacheResponse.ok) {
                  const cacheData = await cacheResponse.json();
                  console.log('✅ Datos obtenidos del caché tras completed:', cacheData);

                  // Debug detallado de la estructura
                  console.log('🔍 COMPLETED HANDLER - Debug detallado:', {
                    'cacheData.response': !!cacheData.response,
                    'cacheData.response.data': !!(cacheData.response && cacheData.response.data),
                    'cacheData.response.data.length': cacheData.response && cacheData.response.data ? cacheData.response.data.length : 'N/A',
                    'cacheData.response.total': cacheData.response && cacheData.response.total,
                    'cacheData.data': !!cacheData.data,
                    'cacheData.total': cacheData.total
                  });

                  const resultado = {
                    datos: {
                      resultados: cacheData.data || (cacheData.response && cacheData.response.data) || [],
                      totales: cacheData.totales || (cacheData.response && cacheData.response.totales) || {},
                    },
                    totalItems: cacheData.total || (cacheData.response && cacheData.response.total) || data.totalRecords || 0,
                    page: cacheData.page || (cacheData.response && cacheData.response.page) || 1,
                    limit: cacheData.limit || (cacheData.response && cacheData.response.limit) || 50,
                    totalTime: data.processingTimeMs || 0,
                    processingStats: {
                      jobId: jobInfo.jobId,
                      cacheKey: cacheKey,
                      duration: data.processingTimeMs || 0,
                      recordsProcessed: cacheData.total || (cacheData.response && cacheData.response.total) || data.totalRecords || 0,
                      fromCache: true
                    }
                  };

                  // Guardar en cache del navegador
                  reportesCache.set(parametros, resultado);

                  resolve(resultado);
                } else {
                  console.warn('⚠️ No se pudieron obtener datos del caché tras completed, intentando llamada síncrona...');

                  // Intentar obtener datos con llamada síncrona como fallback
                  try {
                    const syncParams = { ...parametros };
                    delete syncParams.subscribe; // Remover subscribe para llamada síncrona

                    console.log('🔄 Intentando llamada síncrona fallback tras completed con params:', syncParams);
                    console.log('🔄 URL:', url);

                    const syncResponse = await axios.get(url, {
                      params: syncParams,
                      headers: headers,
                    });

                    console.log('🔄 Respuesta síncrona completa tras completed:', syncResponse.data);

                    const syncDatosDirectos = syncResponse.data.data || (syncResponse.data.response && syncResponse.data.response.data);
                    console.log('🔄 syncDatosDirectos tras completed:', syncDatosDirectos);
                    console.log('🔄 syncDatosDirectos length tras completed:', syncDatosDirectos ? syncDatosDirectos.length : 'N/A');

                    if (syncDatosDirectos && Array.isArray(syncDatosDirectos) && syncDatosDirectos.length > 0) {
                      console.log('✅ Resolviendo con datos del fallback síncrono tras completed');
                      resolve({
                        datos: {
                          resultados: syncDatosDirectos,
                          totales: syncResponse.data.totales || (syncResponse.data.response && syncResponse.data.response.totales) || {},
                        },
                        totalItems: syncResponse.data.total || (syncResponse.data.response && syncResponse.data.response.total) || syncDatosDirectos.length,
                        page: syncResponse.data.page || (syncResponse.data.response && syncResponse.data.response.page) || 1,
                        limit: syncResponse.data.limit || (syncResponse.data.response && syncResponse.data.response.limit) || 50,
                        fromCache: false,
                        fromSyncFallback: true
                      });
                      return;
                    } else {
                      console.log('⚠️ No se encontraron datos en respuesta síncrona tras completed');
                    }
                  } catch (syncError) {
                    console.error('❌ Error en llamada síncrona fallback tras completed:', syncError);
                    console.error('❌ Detalles del error tras completed:', syncError.response ? syncError.response.data : syncError.message);
                  }

                  // Resolver con estructura vacía pero válida
                  resolve({
                    datos: {
                      resultados: [],
                      totales: {}
                    },
                    totalItems: 0,
                    page: 1,
                    limit: 50,
                    fromCache: false,
                    cacheError: true,
                    errorMessage: 'No se pudieron obtener los datos'
                  });
                }
              }).catch(cacheError => {
                console.error('❌ Error al obtener datos del caché tras completed:', cacheError);

                // Resolver con estructura vacía pero válida
                resolve({
                  datos: {
                    resultados: [],
                    totales: {}
                  },
                  totalItems: 0,
                  page: 1,
                  limit: 50,
                  fromCache: false,
                  cacheError: true,
                  errorMessage: 'Error de conexión al obtener datos del caché'
                });
              });
            } else if (data.type === 'error') {
              console.error('❌ Error SSE:', data);
              stopEstimatedProgress();
              clearTimeout(timeoutId);
              eventSource.close();

              // Limpiar referencias
              this.currentEventSource = null;
              this.currentJobId = null;
              this.currentStopEstimatedProgress = null;

              reject(new Error(`Error al procesar: ${data.error || 'Error desconocido'}`));
            }
          } catch (parseError) {
            console.error('Error parsing SSE data:', parseError, event.data);
          }
        };

        // Manejar errores de conexión
        eventSource.onerror = (error) => {
          console.error('❌ Error de conexión SSE:', error);
          console.error('❌ EventSource readyState:', eventSource.readyState);
          console.error('❌ EventSource URL:', fullSseUrl);
          stopEstimatedProgress();
          clearTimeout(timeoutId);
          eventSource.close();

          // Limpiar referencias
          this.currentEventSource = null;
          this.currentJobId = null;

          reject(new Error('Error de conexión SSE'));
        };

      } catch (error) {
        console.error('🚨 Error al configurar SSE:', error);
        reject(error);
      }
    });
  }

  async cancelJob() {
    console.log('🔍 Estado antes de cancelar:', {
      currentJobId: this.currentJobId,
      hasEventSource: !!this.currentEventSource
    });

    if (!this.currentJobId) {
      console.log('⚠️ No hay job activo para cancelar');

      // Cerrar EventSource si existe aunque no tengamos jobId
      if (this.currentEventSource) {
        this.currentEventSource.close();
        this.currentEventSource = null;
        console.log('🔌 EventSource cerrado (sin jobId)');
      }

      // Detener timer de progreso estimado si existe
      if (this.currentStopEstimatedProgress) {
        this.currentStopEstimatedProgress();
        this.currentStopEstimatedProgress = null;
        console.log('⏹️ Timer de progreso estimado detenido (sin jobId)');
      }

      // Limpiar todos los timeouts pendientes
      if (this.currentTimeouts && this.currentTimeouts.length > 0) {
        this.currentTimeouts.forEach(timeout => clearTimeout(timeout));
        this.currentTimeouts = [];
        console.log('🧹 Todos los timeouts limpiados (sin jobId)');
      }

      return false;
    }

    try {
      console.log('⛔ Cancelando job:', this.currentJobId);

      const cancelUrl = `${API_URL}api/v1/reportes-especiales/info-cobranzas/jobs/${this.currentJobId}/cancel`;
      const token = localStorage.getItem('token');

      console.log('📡 URL de cancelación:', cancelUrl);

      const response = await fetch(cancelUrl, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'token': token,
          'Accept': 'application/json'
        }
      });

      console.log('📡 Response status:', response.status, response.statusText);

      if (!response.ok) {
        console.error('❌ Error en respuesta:', response.status, response.statusText);
      }

      const result = await response.json();
      console.log('✅ Resultado de cancelación completo:', result);

      // Detener timer de progreso estimado primero
      if (this.currentStopEstimatedProgress) {
        this.currentStopEstimatedProgress();
        console.log('⏹️ Timer de progreso estimado detenido');
      }

      // Limpiar todos los timeouts pendientes
      if (this.currentTimeouts && this.currentTimeouts.length > 0) {
        this.currentTimeouts.forEach(timeout => clearTimeout(timeout));
        this.currentTimeouts = [];
        console.log('🧹 Todos los timeouts limpiados');
      }

      // Cerrar EventSource si existe
      if (this.currentEventSource) {
        this.currentEventSource.close();
        console.log('🔌 EventSource cerrado');
      }

      // Limpiar referencias siempre
      this.currentEventSource = null;
      this.currentJobId = null;
      this.currentStopEstimatedProgress = null;

      // Verificar diferentes formatos de respuesta del backend
      // Formato 1: {cancelled: true/false}
      // Formato 2: {status: 'success', response: {cancelled: true}}
      // Formato 3: {statusCode: 200, status: 'success'}
      const cancelled = result.cancelled
        || (result.response && result.response.cancelled)
        || (result.status === 'success' && response.ok)
        || response.ok;

      console.log('🔍 Valor de cancelled:', cancelled);

      return cancelled;
    } catch (error) {
      console.error('❌ Error al cancelar job:', error);

      // Detener timer de progreso estimado
      if (this.currentStopEstimatedProgress) {
        this.currentStopEstimatedProgress();
        console.log('⏹️ Timer de progreso estimado detenido tras error');
      }

      // Limpiar todos los timeouts pendientes
      if (this.currentTimeouts && this.currentTimeouts.length > 0) {
        this.currentTimeouts.forEach(timeout => clearTimeout(timeout));
        this.currentTimeouts = [];
        console.log('🧹 Todos los timeouts limpiados tras error');
      }

      // Intentar cerrar EventSource de todas formas
      if (this.currentEventSource) {
        this.currentEventSource.close();
        console.log('🔌 EventSource cerrado tras error');
      }

      // Limpiar referencias
      this.currentEventSource = null;
      this.currentJobId = null;
      this.currentStopEstimatedProgress = null;

      return false;
    }
  }

  async obtenerInfoCteYNoCte(parametros) {
    try {
      const url = `${API_URL}api/v1/reportes-especiales/cte-no-cte`;

      // Agregar filtros para subtotales (el filtrado de activos se hace en frontend)
      const parametrosConFiltrosActivos = {
        ...parametros,
        incluir_subtotales: true, // Incluir subtotales por operatoria
      };

      console.log('🎯 Info Cte y No Cte - Parámetros enviados al endpoint:', parametrosConFiltrosActivos);

      const response = await axios.get(url, {
        params: parametrosConFiltrosActivos,
        headers: {
          'Content-Type': 'application/json',
          token: localStorage.getItem('token'),
        },
      });

      return {
        response: response.data.response, // Estructura original
        datos: {
          resultados: response.data.response.data || [],
          totales: response.data.response.totales || {},
        },
        total: response.data.response.total || (response.data.response.data ? response.data.response.data.length : 0),
        totalItems: response.data.response.total || (response.data.response.data ? response.data.response.data.length : 0),
        page: response.data.response.page || 1,
        limit: response.data.response.limit || 10,
      };
    } catch (error) {
      console.error('Error al obtener info cte y no cte:', error);
      throw error;
    }
  }

  async obtenerFechasDisponiblesCteYNoCte() {
    try {
      const url = `${API_URL}api/v1/reportes-especiales/cte-no-cte/available-dates`;

      const response = await axios.get(url, {
        headers: {
          'Content-Type': 'application/json',
          token: localStorage.getItem('token'),
        },
      });

      return response.data.response;
    } catch (error) {
      console.error('Error al obtener fechas disponibles para Info Cte y No Cte:', error);
      throw error;
    }
  }

  async obtenerSaldosHTC(parametros) {
    try {
      const url = `${API_URL}api/v1/reportes-especiales/saldos-htc`;

      // Agregar filtros para subtotales
      const parametrosConFiltros = {
        ...parametros,
        incluir_subtotales: true, // Incluir subtotales por operatoria
      };


      const response = await axios.get(url, {
        params: parametrosConFiltros,
        headers: {
          'Content-Type': 'application/json',
          token: localStorage.getItem('token'),
        },
      });

      return {
        response: response.data.response, // Estructura original
        datos: {
          resultados: response.data.response.data || [],
          totales: response.data.response.totales || {},
        },
        total: response.data.response.total || (response.data.response.data ? response.data.response.data.length : 0),
        totalItems: response.data.response.total || (response.data.response.data ? response.data.response.data.length : 0),
        page: response.data.response.page || 1,
        limit: response.data.response.limit || 10,
        periodo: response.data.response.periodo || null, // Periodo del reporte HTC
      };
    } catch (error) {
      console.error('Error al obtener saldos HTC:', error);
      throw error;
    }
  }

  async obtenerFechasDisponiblesSaldosHTC() {
    try {
      // Reutilizar el mismo endpoint de fechas disponibles que Info Cte y No Cte
      const url = `${API_URL}api/v1/reportes-especiales/cte-no-cte/available-dates`;

      const response = await axios.get(url, {
        headers: {
          'Content-Type': 'application/json',
          token: localStorage.getItem('token'),
        },
      });

      return response.data.response;
    } catch (error) {
      console.error('Error al obtener fechas disponibles para Saldos HTC:', error);
      throw error;
    }
  }

  async obtenerDepartamentos() {
    try {
      const url = `${API_URL}api/v1/departamentos`;

      const response = await axios.get(url, {
        headers: {
          'Content-Type': 'application/json',
          token: localStorage.getItem('token'),
        },
      });

      return response.data.departamentos || response.data.response || response.data;
    } catch (error) {
      console.error('Error al obtener departamentos:', error);
      throw error;
    }
  }
}

export default new ReportesApiService();
